// import { AxiosResponse, AxiosError } from 'axios';
// interface IAPIResponse<T> {
//   status: 'success' | 'fail';
//   message: string;
//   data: T;
// }
// export const handleApiResponse = <T>(response: AxiosResponse<IAPIResponse<T>>) => {
//   return response.data;
// };
// export const handleApiError = (error: AxiosError) => {
//   if (error.response?.data?.error?.message) {
//     throw error.response.data.error.message;
//   } else {
//     throw error.response?.data.message;
//   }
// };
// export const apiGet = async <T>(
//   endpoint: string,
//   config?: Record<string, unknown>,
// ): Promise<T> => {
//   try {
//     const response = await mirageApi.get(endpoint, config);
//     return handleApiResponse<T>(response);
//   } catch (e) {
//     handleApiError(e);
//   }
// };
// export const getLeague = async (
//     tournamentId: number,
// ): Promise<IAPIResponse<ILeague>> => {
//     return mirageApiGet(`/tournaments/${tournamentId}`);
// };
// export const getTeamRatings = async (
//     teamId: number,
// ): Promise<IAPIResponse<ITeam>> => {
//     return mirageApiGet(`/teams/${teamId}/ratings`);
// };
// export const getLeagues = async (): Promise<IAPIResponse<ILeague[]>> => {
//     return mirageApiGet("/leagues");
// };
// In this example, apiGet is a generic function that handles the common logic for making API calls using axios. The handleApiResponse and handleApiError functions are used to process the API response or handle errors consistently across different API calls. The generic type parameter <T> allows you to specify the type of the expected data in the API response.
import axios, {
    AxiosError,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from "axios";

import { API_URL, SITE_ID } from "./globals";
import { getTokenFromLocalStorage } from "./utils";

import {
    IUserProfileData,
    UserFormData,
} from "../pages/SiteManagement/Users/types/users";
import { ScheduleActionsContextValues } from "../pages/TournamentManagement/components/ScheduleActions/types/contexts";
import {
    IAPIResponse,
    ICOIInfo,
    ICoupon,
    IDesignation,
    IField,
    IGame,
    IGameLog,
    IMasterTournament,
    IMasterTournamentSchedule,
    INotice,
    IOrder,
    IOrderHistory,
    IOrderProduct,
    IOrganization,
    IPhoto,
    IPhotoLocation,
    IPlacement,
    IPlayer,
    IPlayoff,
    IPlayoffBracket,
    IProduct,
    IProductCategory,
    IPromoCode,
    IPromoCodeType,
    ISeason,
    ISite,
    ISiteSetting,
    ITeam,
    ITeamRating,
    ITeamSchedule,
    ITournament,
    ITournamentList,
    ITournamentSchedule,
    ITournamentTemplate,
    IUmpireRating,
    IUser,
    IUserLevel,
    IUserPlacement,
} from "../types/api";
import {
    IClubhouseCOIData,
    IClubhouseTeamInfoData,
    IContactFormData,
    ICreateGameData,
    ICreateProductData,
    ICreatePromoCodeData,
    IGameSummaryData,
    IHomeLeague,
    IHomeLeaguesFormData,
    ILeagueMgmtFieldData,
    ILeagueMgmtOrgInfoData,
    IPhotoUploadFormData,
    IPlayerFormData,
    IRegisterCoach,
    IRegisterTeamForm,
    IRescheduleGameData,
    ISiteNoticeForm,
    ISiteSettingsForm,
    ITournamentContactFormData,
    ITournamentOverviewContactFormData,
    ITourneyMgmtScheduleTemplateData,
    ITourneyMgmtTourneyInfoData,
} from "../types/forms";
import {
    ICredentials,
    ILocalStorageUserData,
    IPasswordResetParams,
} from "../types/users.js";

// Create an authorized API request instance
const api = axios.create({
    baseURL: API_URL,
    timeout: 20000,
    withCredentials: true,
});

const requestInterceptor = (config: InternalAxiosRequestConfig<any>) => {
    const token = getTokenFromLocalStorage();

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers["X-Site-Id"] = SITE_ID;

    const url = new URL(config.url!, config.baseURL);

    if (url.search) {
        url.searchParams.append("tournament", "true");
    } else {
        url.search = "?tournament=true";
    }

    config.url = url.toString();

    return config;
};

const responseInterceptors = {
    response: (response: AxiosResponse<any, any>) => {
        if (process.env.BUILD_ENV !== "production") {
            console.log("API Response:", response.data);
        }

        if (response?.data?.token) {
            const userDataRaw = localStorage.getItem("userData");

            if (userDataRaw) {
                let userData: ILocalStorageUserData = JSON.parse(userDataRaw);
                userData.token = response.data.token;
                localStorage.setItem("userData", JSON.stringify(userData));
            }
        }

        return response;
    },
    error: (error: any) => {
        if (process.env.BUILD_ENV !== "production") {
            console.log("API Error:", error);
            error.response.data.error.message =
                error.response.data.error.message +
                "\n\n\n" +
                `File: ${error.response.data.error.file}\n` +
                `Line: ${error.response.data.error.line}\n` +
                error.response.data.error.stack.join("\n");
        }

        return Promise.reject(error);
    },
};

api.interceptors.request.use(config => requestInterceptor(config));
api.interceptors.response.use(
    responseInterceptors.response,
    responseInterceptors.error,
);

export const getSiteSetting = async (
    settingName?: string,
): Promise<IAPIResponse & { setting: ISiteSetting | Array<ISiteSetting> }> => {
    const urlBase = `/site/settings/${SITE_ID}`;
    const url = settingName ? `${urlBase}/${settingName}` : urlBase;

    return api
        .get(url)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProducts = async (
    activeOnly = true,
): Promise<IAPIResponse & { products: IProduct[] }> => {
    return api
        .get(`/products`, { params: { activeOnly } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProductCategories = async (): Promise<
    IAPIResponse & { categories: IProductCategory[] }
> => {
    return api
        .get(`/products/categories`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProduct = async (
    productId: IProduct["id"],
): Promise<IAPIResponse & { product: IProduct }> => {
    return api
        .get(`/products/${productId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createProduct = async (
    product: ICreateProductData,
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    const {
        ageLevel: { label: ageLevel },
        category,
        qty,
        year,
        price,
        active,
        startDate,
        endDate,
    } = product;
    return api
        .post(
            `/products`,
            {
                ageLevel,
                category,
                qty,
                year,
                price,
                userId,
                active,
                startDate,
                endDate,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateProduct = async (
    product: ICreateProductData,
    productId: IProduct["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    const {
        ageLevel: { label: ageLevel },
        category,
        qty,
        year,
        price,
        active,
        startDate,
        endDate,
        tournamentId,
    } = product;
    return api
        .put(
            `/products/${productId}`,
            {
                ageLevel,
                category,
                qty,
                year,
                price,
                userId,
                active,
                startDate,
                endDate,
                tournamentId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const archiveProduct = async (
    productId: IProduct["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    return api
        .post(
            `/products/${productId}/archive`,
            {
                archive: 1,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const unarchiveProduct = async (
    productId: IProduct["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    return api
        .post(
            `/products/${productId}/archive`,
            {
                archive: 0,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

/* Used for promo code management */
export const getPromoCodes = async (
    params?: object,
    all = false,
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    return api
        .get(`/products/promocodes`, { params: { ...params, all } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

/* Used for promo code management */
export const getPromoCode = async (
    promoCodeId: IPromoCode["id"],
): Promise<IAPIResponse & { promoCode: IPromoCode }> => {
    return api
        .get(`/products/promocodes/${promoCodeId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPromoCodeTypes = async (): Promise<
    IAPIResponse & {
        promoCodeTypes: IPromoCodeType[];
    }
> => {
    return api
        .get(`/products/promocodes/types`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createPromoCode = async (
    product: ICreatePromoCodeData,
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    const {
        code,
        discount,
        description,
        maxUses,
        active,
        startDate,
        endDate,
        appliesTo,
        excludes,
        promoCodeType: { value: promoCodeType },
    } = product;
    return api
        .post(
            `/products/promocodes`,
            {
                code,
                discount,
                description,
                maxUses,
                active,
                startDate,
                endDate,
                appliesTo,
                excludes,
                userId,
                promoCodeType,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updatePromoCode = async (
    product: ICreatePromoCodeData,
    promoCodeId: IPromoCode["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    const {
        code,
        discount,
        description,
        maxUses,
        active,
        startDate,
        endDate,
        appliesTo,
        excludes,
        promoCodeType: { value: promoCodeType },
    } = product;
    return api
        .put(
            `/products/promocodes/${promoCodeId}`,
            {
                code,
                discount,
                description,
                maxUses,
                active,
                startDate,
                endDate,
                appliesTo,
                excludes,
                userId,
                promoCodeType,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const archivePromoCode = async (
    promoCodeId: IPromoCode["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    return api
        .post(
            `/products/promocodes/${promoCodeId}/archive`,
            {
                archive: 1,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const unarchivePromoCode = async (
    promoCodeId: IPromoCode["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    return api
        .post(
            `/products/promocodes/${promoCodeId}/archive`,
            {
                archive: 0,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getFieldsList = async (): Promise<
    IAPIResponse & { fields: IField[] }
> => {
    return api
        .get(`/tournaments/fields`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getField = async (
    fieldId: number,
): Promise<IAPIResponse & { field: IField }> => {
    return api
        .get(`/tournaments/fields/${fieldId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getAgeLevels = async () => {
    try {
        const response = await api.get(`/tournaments/ageLevels`);

        return response.data;
    } catch (e) {
        if (e instanceof AxiosError) {
            if (e.response?.data?.error?.message) {
                throw e.response.data.error.message;
            } else {
                throw e.response?.data.message;
            }
        }
    }
};

export const getPositions = async () => {
    try {
        const response = await api.get(`/teams/positions`);

        return response.data;
    } catch (e) {
        if (e instanceof AxiosError) {
            if (e.response?.data?.error?.message) {
                throw e.response.data.error.message;
            } else {
                throw e.response?.data.message;
            }
        }
    }
};

export const getTournaments = async (): Promise<
    IAPIResponse & { tournaments: ITournament[] }
> => {
    return api
        .get(`/tournaments`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTournament = async (
    tournamentId: number,
): Promise<IAPIResponse & { tournament: ITournament }> => {
    return api
        .get(`/tournaments/${tournamentId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getMasterTournaments = async (): Promise<
    IAPIResponse & { tournaments: IMasterTournament[] }
> => {
    return api
        .get(`/tournaments/masters`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getMasterTournament = async (
    tournamentId: number,
): Promise<IAPIResponse & { tournament: IMasterTournament }> => {
    return api
        .get(`/tournaments/masters/${tournamentId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getAllTeamRatings = async (): Promise<
    IAPIResponse & { ratings: ITeamRating[] }
> => {
    return api
        .get(`/ratings/team`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeamRatings = async (
    teamId: number,
    gameId?: number,
): Promise<IAPIResponse & { ratings: ITeamRating[] }> => {
    const params = gameId && {
        gameId,
    };
    return api
        .get(`/ratings/team/${teamId}`, {
            params,
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUmpireRatings = async (
    umpireId: number,
): Promise<IAPIResponse & { ratings: IUmpireRating[] }> => {
    return api
        .get(`/ratings/umpire/${umpireId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGameRatings = async (
    gameId: number,
    type: "umpire" | "team",
): Promise<IAPIResponse & { ratings: Array<ITeamRating | IUmpireRating> }> => {
    return api
        .get(`/ratings/game/${gameId}`, {
            params: {
                ratingType: type,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTournamentSchedule = async (
    tournamentId: number,
): Promise<
    IAPIResponse & { schedule: ITournamentSchedule | IMasterTournamentSchedule }
> => {
    return api
        .get(`/tournaments/${tournamentId}/schedule`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTournamentPlaceholders = async (
    tournamentId: number,
): Promise<IAPIResponse & { games: IGame[] }> => {
    return api
        .get(`/tournaments/${tournamentId}/placeholders`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getAllTournamentScheduleTemplate = async (): Promise<
    IAPIResponse & { templates: ITournamentTemplate[] }
> => {
    return api
        .get(`/tournaments/template`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTemplate = async (
    param: "template" | "field" | "tournament",
    id: number,
): Promise<IAPIResponse & { template: ITournamentTemplate }> => {
    return api
        .get(`/tournaments/template`, {
            params: {
                [param]: id,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTemplateByTournamentId = async (
    tournamentId: number,
): Promise<IAPIResponse & { template: ITournamentTemplate }> => {
    return api
        .get(`/tournaments/template`, { params: { tournamentId } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTournamentTeams = async (
    tournamentId: number,
): Promise<IAPIResponse & { teams: ITeam[] }> => {
    return api
        .get(`/tournaments/${tournamentId}/teams`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPlayoffSchedule = async (tournamentId: number) => {
    try {
        const response = await api.get(
            `/tournaments/${tournamentId}/schedule?playoffs=true`,
        );

        return response.data;
    } catch (e) {
        if (e instanceof AxiosError) {
            if (e.response?.data?.error?.message) {
                throw e.response.data.error.message;
            } else {
                throw e.response?.data.message;
            }
        }
    }
};

export const getBracket = async (
    tournamentId: number,
): Promise<IAPIResponse & { bracket: IPlayoff | IPlayoff[] }> => {
    return api
        .get(`/tournaments/${tournamentId}/bracket`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeamSchedule = async (
    teamId: number,
): Promise<IAPIResponse & { schedule: ITeamSchedule }> => {
    return api
        .get(`/tournaments/teams/${teamId}/schedule`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGame = async (
    gameId: IGame["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .get(`/games/${gameId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGameLogs = async (
    gameId?: IGame["id"],
): Promise<IAPIResponse & { logs: IGameLog[] }> => {
    const urlBase = `/logs/games`;
    const url = gameId ? `${urlBase}/${gameId}` : urlBase;

    return api
        .get(url)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeams = async (): Promise<
    IAPIResponse & { teams: ITeam[] }
> => {
    return api
        .get(`/teams`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeam = async (
    teamId: number,
): Promise<IAPIResponse & { team: ITeam }> => {
    return api
        .get(`/teams/${teamId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getClubhouseTeams = async (
    userId: number,
    isRegistering = false,
): Promise<IAPIResponse & { teams: ITeam[] }> => {
    return api
        .get(`/teams`, {
            params: {
                userId: userId,
                isRegistering,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getClubhouseTournaments = async (
    userId: number,
): Promise<IAPIResponse & { tournaments: ITournamentList[] }> => {
    return api
        .get(`/teams/tournaments`, {
            params: {
                userId: userId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeamsTournaments = async (
    teamId: number,
): Promise<IAPIResponse & { tournaments: ITournament[] }> => {
    return api
        .get(`/teams/${teamId}/tournaments`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUser = async (
    user: IUser["id"],
): Promise<IAPIResponse & { user: IUser }> => {
    return api
        .get(`/users/${user}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUsers = async (
    userType?: IUserLevel["levelName"],
): Promise<IAPIResponse & { users: IUser[] }> => {
    return api
        .get(`/users`, {
            params: userType
                ? {
                      type: userType,
                  }
                : {},
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getDesignations = async (): Promise<
    IAPIResponse & { designations: IDesignation[] }
> => {
    return api
        .get(`/tournaments/designations`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getSeasons = async (): Promise<
    IAPIResponse & { seasons: ISeason[] }
> => {
    return api
        .get(`/tournaments/seasons`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getBracketTypes = async (): Promise<
    IAPIResponse & { brackets: IPlayoffBracket[] }
> => {
    return api
        .get(`/leagues/brackets`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeamsByAgeLevel = async (
    ageLevel: string,
    year: number,
    season: string,
): Promise<IAPIResponse & { teams: ITeam[] }> => {
    return api
        .get(`/teams`, {
            params: {
                ageLevel,
                year,
                season,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPlayer = async (
    playerId: IPlayer["id"],
): Promise<IAPIResponse & { player: IPlayer }> => {
    return api
        .get(`/players/${playerId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const deletePlayer = async (
    playerId: IPlayer["id"],
    teamId: ITeam["id"],
): Promise<IAPIResponse> => {
    return api
        .delete(`/players/${playerId}`, {
            data: {
                teamId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const verifyPlayer = async (
    playerId: IPlayer["id"],
): Promise<IAPIResponse & { player: IPlayer["id"] }> => {
    return api
        .get(`/players/verify/${playerId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getRoster = async (
    teamId: ITeam["id"],
    tournamentId?: ITournament["id"],
): Promise<IAPIResponse & { players: IPlayer[] }> => {
    return api
        .get(
            `/teams/${teamId}/roster${tournamentId ? `?tournamentId=${tournamentId}` : ""}`,
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUmpires = async (): Promise<
    IAPIResponse & {
        umpires: Array<Omit<IUser, "password" | "meta"> & { name: string }>;
    }
> => {
    return api
        .get(`/users/umpires`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPlacementResults = async (): Promise<
    IAPIResponse & { placements: IPlacement[] }
> => {
    return api
        .get(`/tournaments/placements`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUserPlacementRatings = async (
    userId: number,
): Promise<IAPIResponse & { ratings: IUserPlacement[] }> => {
    return api
        .get(`/tournaments/placements/${userId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updatePlacementRatings = async (
    teamId: number,
    rating: number,
    userId: number,
): Promise<IAPIResponse & { rating: IUserPlacement }> => {
    return api
        .post(
            `/tournaments/placements/`,
            {
                teamId,
                rating,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const assignPlayoffSeeds = (
    tournamentId: number,
    ignoreGames: boolean,
): void => {
    api.post(
        `/tournaments/buildPlayoffs/${tournamentId}`,
        {
            ignoreGames,
        },
        {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        },
    );
};

export const resetPlayoffSeeds = async (
    tournamentId: number,
    userId: number,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/tournaments/resetPlayoffs/${tournamentId}`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPaymentIntent = async (
    cart: IRegisterTeamForm[],
    total: number,
    discount: number,
): Promise<IAPIResponse & { clientSecret: string }> => {
    return api
        .post(
            `/orders/paymentintent/`,
            {
                cart,
                total,
                discount,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getStripeOrder = async (
    paymentIntentId: string,
): Promise<IAPIResponse & { paymentStatus: string; order: IOrder }> => {
    return api
        .post(
            `/orders/stripestatus/`,
            {
                paymentIntentId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const addOrderHistory = async (
    paymentIntentId: string,
    message: string,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/orders/history`,
            {
                paymentIntentId,
                message,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateOrder = async (
    orderId: IOrder["id"],
    orderStatus: IOrder["status"],
    orderNote?: IOrderHistory["message"],
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .put(
            `/orders/${orderId}`,
            {
                orderStatus,
                orderNote,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const cancelProduct = async (
    orderId: IOrder["id"],
    productId: IOrderProduct["id"],
    teamId: number,
    userId: IUser["id"],
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .put(
            `/products/${productId}/cancel`,
            {
                orderId,
                teamId,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const changeProduct = async (
    orderId: IOrder["id"],
    productId: IOrderProduct["id"],
    oldProductId: IOrderProduct["id"],
    teamId: number,
    userId: IUser["id"],
): Promise<IAPIResponse & { order: IOrder; note?: string }> => {
    return api
        .put(
            `/products/${oldProductId}/change`,
            {
                orderId,
                newProductId: productId,
                teamId,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

/* Used during registration process to retrieve promo code */
export const getCoupon = async (
    couponCode: IPromoCode["code"],
): Promise<IAPIResponse & { coupon: ICoupon | null }> => {
    return api
        .get(`/orders/coupon`, { params: { couponCode } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getOrder = async (
    orderId: number,
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .get(`/orders/${orderId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getOrders = async (
    year?: number,
): Promise<IAPIResponse & { orders: IOrder[] }> => {
    return api
        .get(`/orders`, {
            params: {
                year,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getOrganizations = async (
    orgId?: number,
): Promise<
    IAPIResponse &
        Partial<{ organizations: IOrganization[]; organization: IOrganization }>
> => {
    let url = "/organizations";
    url = orgId ? `${url}/${orgId}` : url;

    return api
        .get(url)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getCOI = async (
    teamId?: number,
    status?: string,
): Promise<IAPIResponse & { coi: ICOIInfo[] }> => {
    const URL = teamId ? `/teams/coi/${teamId}` : `/teams/coi`;
    const params = status && { status };

    return api
        .get(URL, { params })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const approveCOI = async (
    teamId: ITeam["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}/approve`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updatePhoto = async (
    photoId: IPhoto["id"],
    location: IPhotoLocation["id"] | null,
    userId: IUser["id"],
): Promise<IAPIResponse & { photo: IPhoto }> => {
    return api
        .put(
            `/site/photos/${photoId}`,
            {
                location,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const rejectCOI = async (
    teamId: ITeam["id"],
    message: string,
    userId: IUser["id"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}/reject`,
            {
                userId,
                message,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const addCOIMessage = async (
    teamId: ITeam["id"],
    message: string,
    userId: IUser["id"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}/message`,
            {
                userId,
                message,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const addCOIExpiration = async (
    teamId: ITeam["id"],
    expiration: string,
    userId: IUser["id"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}/expiration`,
            {
                userId,
                expiration,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const loginUser = async (
    creds: ICredentials,
): Promise<IAPIResponse & ILocalStorageUserData> => {
    return api
        .post(
            `/users/authenticate`,
            {
                remember: creds.remember,
            },
            {
                auth: {
                    username: creds.email,
                    password: creds.password,
                },
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateUser = async (
    user: IUserProfileData,
): Promise<IAPIResponse> => {
    const {
        id,
        firstName,
        lastName,
        phone,
        email,
        password,
        confirmPassword,
        city,
        state,
    } = user;

    return api
        .put(
            `/users/${user.id}`,
            {
                id,
                firstName,
                lastName,
                phone,
                email,
                password,
                confirmPassword,
                city,
                state,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const registerUser = async (
    user: IRegisterCoach,
    userType: "coach",
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users`,
            {
                userType,
                user,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const addUser = async (
    userData: UserFormData,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/add`,
            {
                userData,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const editUser = async (
    userData: UserFormData,
): Promise<IAPIResponse> => {
    const { id, city, email, firstName, lastName, phone, state, permissions } =
        userData;
    return api
        .put(
            `/users`,
            {
                id,
                city,
                email,
                firstName,
                lastName,
                phone,
                state,
                permissions,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const verifyEmail = async (
    email: IUser["email"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/verifyemail`,
            {
                email,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createTournament = async (
    tournament: ITourneyMgmtTourneyInfoData,
    userId: IUser["id"],
): Promise<IAPIResponse & { tournaments: IMasterTournament[] }> => {
    const {
        active,
        ageLevels,
        designation: { value: designation } = {},
        logo,
        photos,
        endDate,
        location: { value: location },
        name,
        description,
        startDate,
        hotelLink,
        showOnUmphub,
        showToPublic,
        poolGames,
        director,
    } = tournament;

    return api
        .post(
            `/tournaments`,
            {
                active,
                ageLevels,
                designation,
                logo,
                photos,
                endDate,
                location,
                name,
                description,
                startDate,
                userId,
                hotelLink,
                showOnUmphub,
                showToPublic,
                poolGames,
                director,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateTournament = async (
    tournamentId: ITournament["id"],
    tournament: ITourneyMgmtTourneyInfoData,
    droppedGames: Array<number>,
): Promise<
    IAPIResponse & { tournaments: ITournament[] | IMasterTournament[] }
> => {
    const {
        active,
        ageLevels,
        teams,
        designation: { value: designation } = {},
        logo,
        photos,
        endDate,
        location,
        name,
        description,
        parent,
        startDate,
        hotelLink,
        showOnUmphub,
        showToPublic,
        poolGames,
        director,
        schedulingData,
        homeLeagues,
        teamCities,
        currentPhotos,
    } = tournament;

    return api
        .put(
            `/tournaments/${tournamentId}`,
            {
                active,
                ageLevels,
                teams,
                designation,
                logo,
                photos,
                endDate,
                location,
                name,
                description,
                parent,
                startDate,
                hotelLink,
                showOnUmphub,
                showToPublic,
                poolGames,
                director,
                schedulingData,
                homeLeagues,
                teamCities,
                currentPhotos,
                droppedGames,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createOrder = async (
    paymentType: string,
    cart: IRegisterTeamForm[],
    cartTotal: number,
    discount: number,
    notes?: string,
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .post(
            `/orders`,
            {
                paymentType,
                cart,
                cartTotal,
                discount,
                notes,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    return Promise.reject(e.response.data.error.message);
                } else {
                    return Promise.reject(e.response?.data.message);
                }
            }
            return Promise.reject("An unexpected error occurred.");
        });
};

export const updateTeam = async (
    teamId: ITeam["id"],
    team: IClubhouseTeamInfoData,
): Promise<IAPIResponse & { team: ITeam }> => {
    const {
        name,
        coach: { value: coach },
        bannedUmpire1,
        bannedUmpire2,
        files,
        settings,
    } = team;

    const banned1 = bannedUmpire1 ? bannedUmpire1.value : null;
    const banned2 = bannedUmpire2 ? bannedUmpire2.value : null;

    return api
        .put(
            `/teams/${teamId}`,
            {
                name,
                coach,
                bannedUmpire1: banned1,
                bannedUmpire2: banned2,
                files,
                settings,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const uploadPhotos = async (
    data: IPhotoUploadFormData,
): Promise<IAPIResponse> => {
    const {
        firstName,
        lastName,
        email,
        teamName,
        ageLevel: { value: ageLevel },
        files,
    } = data;

    return api
        .post(
            `/site/photoupload`,
            {
                firstName,
                lastName,
                email,
                teamName,
                ageLevel,
                files,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const editorImgUpload = async (file: File): Promise<string> => {
    return api
        .post(
            `/site/photoupload`,
            {
                files: [file],
                editorUpload: true,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data.location;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPhotos = async (
    location?: string,
): Promise<IAPIResponse & { photos: IPhoto[] }> => {
    return api
        .get(`/site/photos`, { params: { location } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const printRules = async (
    content: string,
): Promise<IAPIResponse & { file: string }> => {
    return api
        .get(`/printer`, { params: { content } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const rejectPhoto = async (
    photoId: IPhoto["id"],
    userId: ILocalStorageUserData["user"]["id"],
): Promise<IAPIResponse> => {
    return api
        .delete(`/site/photos/${photoId}`, {
            data: {
                userId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPhotoLocations = async (): Promise<
    IAPIResponse & { photoLocations: IPhotoLocation[] }
> => {
    return api
        .get(`/site/photolocations`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createField = async (
    field: ILeagueMgmtFieldData,
): Promise<IAPIResponse & { fields: IField[] }> => {
    const { active, name, address, city, state, zip, diamonds } = field;

    return api
        .post(
            `/tournaments/fields`,
            {
                active,
                name,
                address,
                city,
                state,
                zip,
                diamonds,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateField = async (
    field: ILeagueMgmtFieldData,
    fieldId: IField["id"],
): Promise<IAPIResponse & { fields: IField[] }> => {
    const { active, name, address, city, state, zip, diamonds } = field;

    return api
        .put(
            `/tournaments/fields/${fieldId}`,
            {
                active,
                name,
                address,
                city,
                state,
                zip,
                diamonds,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createGame = async (
    game: ICreateGameData,
    tournament: IMasterTournament["id"],
    userId: ILocalStorageUserData["user"]["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    const { start, end, home, visitor, field, diamond, note } = game;

    return api
        .post(
            `/games`,
            {
                datetime: start,
                endDate: end,
                home,
                visitor,
                field,
                // API expects this format, in case you're wondering in the future
                diamond: {
                    value: diamond,
                },
                tournament,
                userId,
                note,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createPlaceholder = async (
    tournamentId: number,
    date: IGame["date"],
    endDate: IGame["endDate"],
    ageLevel: IGame["ageLevel"]["ageLevel"],
    field: IGame["fieldId"],
    diamond: IGame["diamond"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .post(
            `/tournaments/placeholders`,
            {
                tournamentId,
                date,
                endDate,
                ageLevel,
                field,
                diamond,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const movePlaceholder = async (props: {
    prev: {
        tournamentId: number;
        field: IGame["fieldId"];
        diamond: IGame["diamond"];
        date: IGame["date"];
        endDate: IGame["endDate"];
        ageLevel: IGame["ageLevel"]["ageLevel"];
    };
    newInfo: {
        date: IGame["date"];
        endDate: IGame["endDate"];
        diamond: IGame["diamond"];
    };
}): Promise<IAPIResponse & { game: IGame }> => {
    const { prev, newInfo } = props;
    return api
        .put(
            `/tournaments/placeholders`,
            {
                prev,
                new: newInfo,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const deletePlaceholder = async (props: {
    tournamentId: number;
    field: IGame["fieldId"];
    diamond: IGame["diamond"];
    date: IGame["date"];
    endDate: IGame["endDate"];
    ageLevel: IGame["ageLevel"]["ageLevel"];
}): Promise<IAPIResponse> => {
    const { tournamentId, field, diamond, date, endDate, ageLevel } = props;
    return api
        .delete(`/tournaments/placeholders`, {
            data: {
                tournamentId,
                ageLevel,
                date,
                endDate,
                field,
                diamond,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const deleteGame = async (
    gameId: IGame["id"],
    userId: ILocalStorageUserData["user"]["id"],
): Promise<
    IAPIResponse & { schedule: ITeamSchedule | ITournamentSchedule }
> => {
    return api
        .delete(`/games/${gameId}`, {
            data: {
                userId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const postponeGame = async (
    gameId: IGame["id"],
    tournamentId: IGame["tournament"],
    userId: ILocalStorageUserData["user"]["id"],
    scheduleType: "team" | "tournament",
    teamId?: ITeam["id"],
): Promise<
    IAPIResponse & { schedule: ITeamSchedule | ITournamentSchedule }
> => {
    return api
        .put(
            `/games/${gameId}/postpone`,
            {
                userId,
                teamId,
                tournamentId,
                scheduleType,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const rescheduleGame = async (
    game: IRescheduleGameData,
    userId: ILocalStorageUserData["user"]["id"],
): Promise<IAPIResponse & { schedule: ITeamSchedule }> => {
    const { gameId, start, end, field, note } = game;

    const diamond = game.diamond || undefined;

    return api
        .put(
            `/games/${gameId}/reschedule`,
            {
                datetime: start,
                endDate: end || undefined,
                field,
                diamond,
                userId,
                note,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createPlayer = async (
    player: IPlayerFormData,
    teamId: ITeam["id"],
): Promise<IAPIResponse & { player: IPlayer }> => {
    const {
        active,
        firstName,
        lastName,
        uniformNumber,
        dob,
        email,
        positions,
        isPrimary,
    } = player;

    return api
        .post(
            `/players`,
            {
                active,
                firstName,
                lastName,
                uniformNumber,
                dob,
                email,
                positions,
                teamId,
                isPrimary,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updatePlayer = async (
    player: IPlayerFormData,
    teamId: ITeam["id"],
    playerId: IPlayer["id"],
): Promise<IAPIResponse & { player: IPlayer }> => {
    const {
        active,
        firstName,
        lastName,
        uniformNumber,
        dob,
        email,
        positions,
        isPrimary,
    } = player;

    return api
        .put(
            `/players/${playerId}`,
            {
                active,
                firstName,
                lastName,
                uniformNumber,
                dob,
                email,
                positions,
                teamId,
                isPrimary,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateSettings = async (
    data: Partial<ISiteSettingsForm>,
): Promise<IAPIResponse> => {
    return api
        .put(
            `/site/settings/${SITE_ID}`,
            {
                data,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createOrganization = async (
    organization: ILeagueMgmtOrgInfoData,
    userId: IUser["id"],
): Promise<IAPIResponse & { organization: IOrganization }> => {
    const {
        name,
        primaryUser: { value: primaryUser },
        majorTeams,
        WITeams,
        ILTeams,
    } = organization;

    return api
        .post(
            `/organizations`,
            {
                name,
                primaryUser,
                majorTeams,
                WITeams,
                ILTeams,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateOrganization = async (
    orgId: IOrganization["id"],
    organization: ILeagueMgmtOrgInfoData,
    userId: IUser["id"],
): Promise<IAPIResponse & { organization: IOrganization }> => {
    const {
        name,
        primaryUser: { value: primaryUser },
        majorTeams,
        WITeams,
        ILTeams,
        approved,
    } = organization;

    return api
        .put(
            `/organizations/${orgId}`,
            {
                name,
                primaryUser,
                majorTeams,
                WITeams,
                ILTeams,
                approved,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateGame = async (
    game: Partial<IGameSummaryData>,
    gameId: IGame["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    const { scores, plateUmpire, fieldUmpire, teams } = game;

    return api
        .put(
            `/games/${gameId}/summary`,
            {
                teams,
                scores,
                plateUmpire,
                fieldUmpire,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateCOI = async (
    teamId: ITeam["id"],
    files: IClubhouseCOIData["files"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}`,
            {
                files,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const lostPassword = async (
    email: ICredentials["email"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/lostpassword/`,
            {
                email,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const sendWaiver = async (
    teamId: ITeam["id"],
    tournamentId: ITournament["id"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/teams/${teamId}/waiver`,
            {
                // the API expects this property to be called leagueId
                leagueId: tournamentId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const acceptWaiver = async (
    playerId: IPlayer["id"],
): Promise<IAPIResponse> => {
    return api
        .post(`/players/${playerId}/waiver`, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const resetPassword = async ({
    userId,
    password,
    confirmPassword,
}: IPasswordResetParams): Promise<IAPIResponse & { email: IUser["email"] }> => {
    return api
        .post(
            `/users/resetpassword`,
            {
                userId,
                password,
                confirmPassword,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const validateTokens = async (): Promise<IAPIResponse> => {
    return api
        .get(`/users/validatetokens`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const validateResetToken = async (
    token: string,
    userId: string,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/validateresettoken`,
            {
                token,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const submitContact = async (
    data:
        | IContactFormData
        | ITournamentContactFormData
        | ITournamentOverviewContactFormData,
): Promise<IAPIResponse> => {
    const {
        comment,
        email,
        firstName,
        lastName,
        tournament: { label: tournament },
    } = data;

    const isIContactFormData = (data: any): data is IContactFormData =>
        "contactType" in data;

    const notOverview = (
        data: any,
    ): data is IContactFormData | ITournamentContactFormData =>
        "ageLevel" in data;

    return api
        .post(
            `/home/contact`,
            {
                ageLevel: notOverview(data) ? data.ageLevel.label : undefined,
                comment,
                contactType: isIContactFormData(data)
                    ? data.contactType
                    : "tournamentDirect",
                email,
                firstName,
                lastName,
                teamName: notOverview(data) ? data.teamName : undefined,
                tournament,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateStandings = async (
    tournamentId: ITournament["id"],
    userId: IUser["id"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/tournaments/standings?tournamentId=${tournamentId}`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getNotice = async (): Promise<
    IAPIResponse & { notice: INotice | false }
> => {
    return api
        .get(`/site/notice/${SITE_ID}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateNotice = async (
    notice: ISiteNoticeForm,
): Promise<IAPIResponse & { notice: INotice | false }> => {
    const { siteNotice } = notice;
    return api
        .put(
            `/site/notice`,
            {
                siteNotice,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const generateTournamentSchedule = async (
    tournamentId: number,
    userId: number,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/tournaments/generate/${tournamentId}`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const generatePlaceholderSchedule = async (
    tournamentId: number,
    userId: number,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/tournaments/placeholders/${tournamentId}/generate`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const clearPlaceholderSchedule = async (
    tournamentId: number,
    userId: number,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/tournaments/placeholders/${tournamentId}/clear`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const confirmPlaceholderSchedule = async (
    tournamentId: number,
): Promise<IAPIResponse> => {
    return api
        .post(`/tournaments/placeholders/${tournamentId}/confirm`, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const unconfirmUmpireSchedule = async (
    tournamentId: number,
): Promise<IAPIResponse> => {
    return api
        .post(`/tournaments/${tournamentId}/unconfirm`, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateTemplate = async (
    templateId: ITournamentTemplate["id"],
    templateData: ITourneyMgmtScheduleTemplateData,
): Promise<IAPIResponse & { template: ITournamentTemplate }> => {
    return api
        .put(
            `/tournaments/template/${templateId}`,
            {
                data: templateData,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const resetTournamentSchedule = async (
    tournamentId: number,
    userId: number,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/tournaments/reset/${tournamentId}`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getHomeLeagues = async (): Promise<
    IAPIResponse & { homeLeagues: IHomeLeague[] }
> => {
    return api
        .get(`/tournaments/homeLeagues`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateHomeLeagues = async (
    homeLeagues: IHomeLeaguesFormData,
): Promise<IAPIResponse & { homeLeagues: IHomeLeague[] }> => {
    return api
        .put(
            `/tournaments/homeLeagues`,
            {
                homeLeagues: homeLeagues.leagues,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const runScheduleActions = async (
    tournamentId: IMasterTournament["id"],
    props: Partial<ScheduleActionsContextValues>,
): Promise<IAPIResponse & { schedule: IMasterTournamentSchedule }> => {
    return api
        .put(`/tournaments/${tournamentId}/schedule`, props, {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const switchToUser = async (
    userId: IUser["id"],
): Promise<IAPIResponse & ILocalStorageUserData> => {
    return api
        .post(`/users/switch/${userId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getSites = async (): Promise<
    IAPIResponse & { sites: ISite[] }
> => {
    return api
        .get(`/site`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUserLevels = async (): Promise<
    IAPIResponse & { userLevels: IUserLevel[] }
> => {
    return api
        .get(`/users/levels`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export default api;
